// The text of each Policy chat card, for the chat PDF export: what the card
// shows, in the same words, without its controls. PolicyChatCard.jsx uses the
// same helpers, so the two say the same thing.
import { valueAt } from "../catalogDocument.js";
import { AGENT_IDS, AGENT_LABELS } from "./changes.js";
import {
    changeOf, changesFor, getSession, inScope, parseCardKey, planSteps, stepById, summarize, warnings,
} from "./session.js";
import { SOURCES } from "./sources.js";

export const labelOf = (agentId) => AGENT_LABELS[agentId] ?? agentId;
export const keyName = (pointer) => pointer.slice(1).split("/").join(".");
export const show = (value) => (value === undefined ? "Not set" : JSON.stringify(value));
export const count = (n, one, many) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

/// A ClickHouse UTC time ("2026-10-07 19:16:38.465") as a local date and time.
export function lastUsed(value) {
    const at = new Date(`${String(value).replace(" ", "T")}${/[zZ]|[+-]\d\d:?\d\d$/.test(value) ? "" : "Z"}`);
    return Number.isNaN(at.getTime()) ? String(value) : at.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/// The detail line under a data row: usage counts and agents.
export function rowMeta(r) {
    if (r.cases !== undefined) return [count(r.cases, "Triage case", "Triage cases"), r.rules?.length ? r.rules.map((x) => x.target).join(", ") : ""].filter(Boolean).join(". ");
    if (r.last !== undefined) return [count(r.sessions, "session", "sessions"), count(r.calls, "tool call", "tool calls"), r.last ? `last used ${lastUsed(r.last)}` : ""].filter(Boolean).join(", ");
    const agents = Object.entries(r.agents ?? {}).map(([a, n]) => `${labelOf(a)} ${n.toLocaleString()}`).join(", ");
    const counts = r.endpoints == null ? "" : [count(r.endpoints, "endpoint", "endpoints"), r.calls != null ? count(r.calls, "tool call", "tool calls") : ""].filter(Boolean).join(", ");
    return [counts, agents ? `by agent: ${agents}` : ""].filter(Boolean).join(". ");
}

const NOT_SAVED = { label: "Policy chat", lines: ["This policy chat's cards were not saved with the chat. Start a new one from Agent Policy."] };

function stepText(session, stepId, instance) {
    const step = stepById(session, stepId);
    if (!step) return null;
    const scope = step.scope === "all" ? "" : ` · ${labelOf(step.scope)} only`;
    const answered = session.done.includes(stepId);
    const current = session.presented?.[stepId] === instance && session.cur === stepId;
    const lines = [step.question];
    if (answered && !current) lines.push(`Answer: ${summarize(session, step)}`);
    else if (current) lines.push("Not answered yet.", ...warnings(session).map((w) => w.text));
    else if (!planSteps(session).some((s) => s.id === stepId)) lines.push("Not used. An earlier answer changed.");
    else lines.push("Not answered.");
    return { label: `Step${scope}${answered && !current ? " · answered" : ""}`, lines };
}

function viewText(session, index) {
    const view = session.views[index];
    if (!view) return null;
    const rows = view.rows.map((r) => [r.name, rowMeta(r)].filter(Boolean).join(": "));
    return { label: `Data · ${SOURCES[view.source].title}`, lines: rows.length ? rows : ["No rows match."] };
}

function reviewText(session) {
    const change = changeOf(session);
    const lines = ["Settings this change writes"];
    for (const a of AGENT_IDS.filter((x) => change.support[x].level !== "no")) {
        const rows = changesFor(session, a);
        if (!inScope(session, a)) { lines.push(`${labelOf(a)}: not included.`); continue; }
        if (!rows.length) { lines.push(`${labelOf(a)}: no change.`); continue; }
        const doc = session.today?.[a]?.document ?? {};
        lines.push(`${labelOf(a)} · ${count(rows.length, "setting", "settings")}`);
        for (const { pointer, value } of rows) {
            lines.push(keyName(pointer), `  Today: ${show(valueAt(doc, pointer))}`, `  After: ${value === undefined ? "Removed" : show(value)}`);
        }
        lines.push(...change.effects(session.answers, a, Object.values(session.rows).flat()));
    }
    if (session.notes.length) lines.push("Notes from the chat:", ...session.notes.map((n) => n.text));
    return { label: "Review", lines };
}

function pilotText(session) {
    const agents = AGENT_IDS.filter((a) => changesFor(session, a).length);
    const lines = ["Start a pilot for each agent"];
    for (const a of agents) {
        if (session.today[a]?.openPilot) lines.push(`${labelOf(a)}: has an open pilot.`);
        else lines.push(`${labelOf(a)}: ${session.pilot[a]?.on ? "included" : "not included"}.`);
    }
    const endpoints = session.pilot[agents[0]]?.endpoints ?? [];
    lines.push(`${count(endpoints.length, "endpoint", "endpoints")} selected${endpoints.length ? ":" : "."}`);
    for (const e of endpoints) lines.push([e.hostname || e.id, e.platform].filter(Boolean).join(", "));
    return { label: "Pilot", lines };
}

function doneText(session) {
    const lines = (session.started ?? []).map((r) => (r.ok
        ? `${labelOf(r.agentId)}: pilot started on ${count(r.endpoints, "endpoint", "endpoints")}.`
        : `${labelOf(r.agentId)}: no pilot started. ${r.rejections?.length ? "The server refused the policy." : r.error || "The start failed."}`));
    return { label: "Pilots", lines };
}

/// `{ label, lines }` for the card a Policy chat job key names, or null when
/// the key names no card.
export function policyCardText(cardKey) {
    const { sessionId, card } = parseCardKey(cardKey);
    const session = getSession(sessionId);
    if (!session) return NOT_SAVED;
    if (card === "pick") {
        const change = changeOf(session);
        return { label: "Change", lines: ["What do you want to change?", change ? `Chosen: ${change.title}` : "Not chosen yet."] };
    }
    if (card.startsWith("step:")) {
        const [, stepId, instance] = card.split(":");
        return stepText(session, stepId, Number(instance));
    }
    if (card.startsWith("view:")) return viewText(session, Number(card.slice(5)));
    if (card === "review" && changeOf(session)) return reviewText(session);
    if (card === "pilot") return pilotText(session);
    if (card === "done") return doneText(session);
    return null;
}
